import { Alert, Box, Stack, TextField, Typography } from "@mui/material"
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"

import { requestLoginCode, verifyLoginCode } from "../../../data-utils/data"
import { setPlayerSession } from "../../../data-utils/playerSessionStore"
import { LOGIN_WORDMARK, ROUTE_PATHS } from "../../../utilities/constants.js"
import { UiCtButton } from "../../common/MuiStyled/LibComponent.jsx"
import logger from "../../../Logger/index.js"

const pageLogger = logger.child({ module: "email code sign-in" })

/**
 * Email-OTP sign-in for players.
 *
 * Replaces the IdP's passwordless/magic-link flow with a 6-digit code that the
 * ChaosTrack server emails through the normal Postmark templates. Two reasons this
 * lives in our own UI rather than on the IdP's hosted pages:
 *
 *  1. Keycloak has no email-OTP authenticator (verified against 26.4: the only OTP
 *     authenticators are TOTP/HOTP for authenticator apps), so there is nothing to
 *     host there without writing a Java extension.
 *  2. Rendering it here means the sign-in screen keeps the product's own look, and
 *     the email keeps the product's own template — both requirements.
 *
 * SECURITY NOTES FOR READERS
 * --------------------------
 * This component intentionally shows the SAME "code sent" state whether or not the
 * address has an account (the server enforces that too). Do not "improve" it by
 * surfacing an "unknown email" message: that turns the sign-in box into a customer
 * -list oracle. Likewise, wrong-code / expired / too-many-attempts are deliberately
 * one indistinguishable message.
 */

/**
 * Visual tokens copied from the Keycloak login theme
 * (infra/keycloak/themes/chaostrack/login/resources/css/chaostrack.css) so the
 * email-code screen is indistinguishable from the hosted sign-in page.
 *
 * They are duplicated rather than imported because the two live in different
 * deployables — one is a Freemarker theme served by Keycloak, this is a React bundle.
 * If the theme's palette changes, change it here too; that is the cost of the split,
 * and it is cheaper than the alternative of routing players through a page that looks
 * like a different product mid-sign-in.
 */
const LOGIN = {
  pageBg: "#1e1e1e",
  card: "#ffffff",
  ink: "#1e212a",
  ink2: "#65676e",
  fieldBorder: "#c9cace",
  fieldBorderFocus: "#1e212a",
  primary: "#f6ab36",
  primaryHover: "#eaa02b",
  primaryInk: "#1e212a",
  link: "#635dff",
  danger: "#d03408",
  font: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
}

/** 56px bordered box with an in-field placeholder — no floating label, as on the IdP page. */
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 56,
    borderRadius: "5px",
    backgroundColor: LOGIN.card,
    fontFamily: LOGIN.font,
    fontSize: 16,
    color: LOGIN.ink,
    "& fieldset": { borderColor: LOGIN.fieldBorder },
    "&:hover fieldset": { borderColor: LOGIN.fieldBorder },
    "&.Mui-focused fieldset": { borderColor: LOGIN.fieldBorderFocus, borderWidth: 1 },
  },
  "& .MuiOutlinedInput-input": { padding: "0 16px", height: 56, boxSizing: "border-box" },
  "& .MuiOutlinedInput-input::placeholder": { color: LOGIN.ink2, opacity: 1 },
}

const buttonSx = {
  height: 56,
  width: "100%",
  borderRadius: "5px",
  backgroundColor: LOGIN.primary,
  color: LOGIN.primaryInk,
  fontFamily: LOGIN.font,
  fontWeight: 400,
  fontSize: 16,
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { backgroundColor: LOGIN.primaryHover, boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: LOGIN.primary, color: LOGIN.primaryInk, opacity: 0.55 },
}

const linkSx = {
  background: "none",
  border: "none",
  p: 0,
  fontFamily: LOGIN.font,
  fontSize: 14,
  cursor: "pointer",
  color: LOGIN.link,
}

const CODE_LENGTH = 6
const RESEND_COOLDOWN_SECONDS = 30

/**
 * `initialEmail`   pre-fills the address, for flows that already know who is signing in
 *                  (an invite link carries the invitee's address — making them retype it
 *                  is friction for no gain).
 * `onAuthenticated` replaces the default "go to the exercise list" redirect. The invite
 *                  flow uses it to resolve the invite token and land on the right
 *                  session instead. Called with the established player session.
 */
export const EmailCodeSignIn = ({ onCancel, initialEmail = "", onAuthenticated, standalone = false }) => {
  const navigate = useNavigate()

  const [step, setStep] = useState("email") // "email" | "code"
  const [email, setEmail] = useState(initialEmail)
  const [challengeId, setChallengeId] = useState(null)
  const [code, setCode] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [notice, setNotice] = useState(null)
  const [cooldown, setCooldown] = useState(0)

  const codeInputRef = useRef(null)

  // Resend cooldown. Client-side courtesy only — the real limit is the server's
  // per-email/per-IP window, which this cannot bypass.
  useEffect(() => {
    if (cooldown <= 0) return undefined
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  useEffect(() => {
    if (step === "code") codeInputRef.current?.focus()
  }, [step])

  const sendCode = useCallback(async (targetEmail) => {
    setBusy(true)
    setError(null)
    const result = await requestLoginCode({ email: targetEmail })
    setBusy(false)

    if (result.rateLimited) {
      setError(result.message)
      return
    }
    if (!result.ok) {
      setError(result.message ?? "Unable to send a code right now. Please try again.")
      return
    }

    // Advance even when the server withheld a challengeId (unknown address,
    // elevated role, rate-limited email). The user sees an identical screen either
    // way; only someone who actually received a code can go further.
    setChallengeId(result.challengeId ?? null)
    setStep("code")
    setCooldown(RESEND_COOLDOWN_SECONDS)
    setNotice(result.message ?? "If that email address has a ChaosTrack account, a sign-in code is on its way.")
  }, [])

  // An invite link already carries the invitee's address, so showing them a pre-filled
  // box and asking them to press Continue is a step that decides nothing. Send the code
  // on arrival and open on the code screen instead.
  //
  // Guarded by a ref rather than state: StrictMode runs effects twice in development,
  // and a second request would burn one of the server's per-email rate-limit slots. A
  // failure leaves the email step visible with its error, so the user can still retry
  // or correct the address.
  const autoSentRef = useRef(false)
  useEffect(() => {
    if (!initialEmail || autoSentRef.current) return
    autoSentRef.current = true
    sendCode(initialEmail)
  }, [initialEmail, sendCode])

  const handleEmailSubmit = async (event) => {
    event.preventDefault()
    const trimmed = email.trim()
    if (!trimmed) return
    await sendCode(trimmed)
  }

  const handleCodeSubmit = async (event) => {
    event.preventDefault()
    if (code.length !== CODE_LENGTH) return

    // No challengeId means the server never issued one for this address. Show the
    // same failure text as a wrong code rather than revealing why.
    if (!challengeId) {
      setError("That code is not valid or has expired. Request a new one.")
      return
    }

    setBusy(true)
    setError(null)
    const result = await verifyLoginCode({ challengeId, code })
    setBusy(false)

    if (result.usePassword) {
      setError(result.message)
      return
    }
    if (!result.ok) {
      setError(result.message)
      setCode("")
      codeInputRef.current?.focus()
      return
    }

    try {
      setPlayerSession(result.session)
    } catch (err) {
      pageLogger.error(`Unable to persist player session: ${err.message}`)
      setError("Your browser blocked session storage, so we can't keep you signed in. Check your privacy settings.")
      return
    }

    pageLogger.info("Player session established via email code")
    if (onAuthenticated) {
      await onAuthenticated(result.session)
      return
    }
    navigate(ROUTE_PATHS.GAME_ROOT)
  }

  const handleResend = async () => {
    if (cooldown > 0) return
    setCode("")
    await sendCode(email.trim())
  }

  const form = (
    <Box component="form" onSubmit={step === "email" ? handleEmailSubmit : handleCodeSubmit} sx={{ width: "100%" }}>
      <Typography
        component="h1"
        sx={{
          fontFamily: LOGIN.font,
          fontWeight: 400,
          fontSize: 28,
          lineHeight: 1.25,
          letterSpacing: "-0.2px",
          color: LOGIN.ink,
          textAlign: "center",
        }}
      >
        {step === "email" ? "Welcome" : "Check your email"}
      </Typography>

      <Typography sx={{ fontFamily: LOGIN.font, fontSize: 15, lineHeight: 1.5, color: LOGIN.ink2, textAlign: "center", mt: "12px", mb: "28px" }}>
        {step === "email" ? "Enter your email address and we'll send you a sign-in code." : notice}
      </Typography>

      <Stack sx={{ gap: 2 }}>
        {step === "email" ? (
          <TextField
            autoFocus
            fullWidth
            type="email"
            placeholder="Email address*"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputProps={{ maxLength: 320, autoComplete: "email", "aria-label": "Email address" }}
            disabled={busy}
            sx={fieldSx}
          />
        ) : (
          <TextField
            inputRef={codeInputRef}
            fullWidth
            placeholder="6-digit code*"
            value={code}
            // Strip non-digits as the user types so a pasted "123 456" or "123-456"
            // still works instead of silently failing validation.
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
            inputProps={{
              inputMode: "numeric",
              autoComplete: "one-time-code",
              maxLength: CODE_LENGTH,
              "aria-label": "6-digit code",
              style: { letterSpacing: "0.4em", textAlign: "center" },
            }}
            disabled={busy}
            sx={fieldSx}
          />
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              fontFamily: LOGIN.font,
              fontSize: 14,
              py: 0,
              borderRadius: "5px",
              color: LOGIN.danger,
              backgroundColor: "#fdeceb",
              "& .MuiAlert-icon": { color: LOGIN.danger },
            }}
          >
            {error}
          </Alert>
        )}

        <UiCtButton type="submit" variant="contained" disabled={busy || (step === "code" && code.length !== CODE_LENGTH)} sx={buttonSx}>
          {busy ? "Please wait…" : step === "email" ? "Continue" : "Sign in"}
        </UiCtButton>

        {step === "code" && (
          <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center" }}>
            <Typography
              component="button"
              type="button"
              onClick={handleResend}
              disabled={cooldown > 0 || busy}
              sx={{ ...linkSx, cursor: cooldown > 0 ? "default" : "pointer", color: cooldown > 0 ? LOGIN.ink2 : LOGIN.link }}
            >
              {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={() => {
                setStep("email")
                setCode("")
                setError(null)
                setChallengeId(null)
              }}
              sx={linkSx}
            >
              Use a different email
            </Typography>
          </Stack>
        )}

        {onCancel && (
          <Typography component="button" type="button" onClick={onCancel} sx={{ ...linkSx, color: LOGIN.ink2, textAlign: "center" }}>
            Back to sign-in options
          </Typography>
        )}
      </Stack>
    </Box>
  )

  // Inside the login page there is already a branded card, so the form alone is right.
  // On its own route (an invite link) nothing else is on screen, so it renders the same
  // dark page and white card the identity provider serves.
  if (!standalone) return form

  return (
    <Box sx={{ background: LOGIN.pageBg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 16px" }}>
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          background: LOGIN.card,
          borderRadius: "5px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.32)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ padding: "40px 40px 0", textAlign: "center" }}>
          <Box
            component="img"
            src={LOGIN_WORDMARK}
            alt="ChaosTrack"
            sx={{ width: 230, maxWidth: "100%", height: 44, objectFit: "contain", display: "block", margin: "0 auto 26px" }}
          />
        </Box>
        <Box sx={{ padding: "0 40px 40px" }}>{form}</Box>
      </Box>
    </Box>
  )
}

export default EmailCodeSignIn
